import { useCallback, useState } from "react"

import { Channel, MediaType } from "../types"
import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { queryCache, useMutation, useQuery } from "react-query"
import { Switch } from "@material-ui/core"
import { Button } from "ui/buttons/Button"
import { CreatePackageModal } from "./CreateModal"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import { ReactComponent as EditIcon } from "assets/icon-edit.svg"
import { Loading } from "components/Loading"
import MoreActions from "components/MoreActions"
import { UpdatePackageModal } from "./UpdateModal"
import { css } from "@emotion/css"
import { deleteVendorChannel, updateVendorChannel } from "api/suppliers"
import { fetchMediaTypeChannels } from "api/globalMediaTypes"
import { useFeedback } from "components/FeedbackSnackbar"
import { useI18n } from "providers/I18n"
import { useToggle } from "hooks/useToggle"

type Props = {
  mediaType: MediaType
  configOwnerId: string
  configScope: string
}

export default function Channels(props: Props) {
  const { t } = useI18n()
  const { mediaType, configOwnerId, configScope } = props
  const isMediaTypeTv = mediaType?.semanticCode === "tv"
  const { open, onOpen, onClose } = useToggle()
  const [channel, setChannel] = useState<Channel>()
  const [modalInfo, setModalInfo] = useState<ModalInfo>(undefined)
  const isModalOpen = Boolean(modalInfo)
  const sendFeedback = useFeedback((s) => s.send)

  const [deleteChannel] = useMutation(deleteVendorChannel, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("media-types-channels")
    },
    onError: (e) => {
      setModalInfo(undefined)
      sendFeedback(t(e.message), "error")
    },
  })
  const onDelete = useCallback(
    (id: string) => {
      setModalInfo({
        title: t("products.list.delete.title"),
        type: "primary_destructive",
        description: t("products.list.delete.description"),
        submitLabel: t("products.list.delete.action"),
        onSubmit: async () => await deleteChannel(id),
      })
    },
    [deleteChannel, t],
  )

  const [patch] = useMutation(updateVendorChannel, {
    onSuccess: () => {
      setModalInfo(undefined)
      queryCache.invalidateQueries("media-types-channels")
    },
    onError: (e) => {
      setModalInfo(undefined)
      sendFeedback(
        t(e.message) || t("vendors.vendorProduct.update.error"),
        "error",
      )
    },
  })

  const onUpdateElectronicOrder = useCallback(
    (id: string, name: string, electronicOrderingActive: boolean) => {
      if (electronicOrderingActive === false) {
        // TODO: Add on the translation service these labels/texts
        setModalInfo({
          title: "Enable Electronic Ordering",
          type: "primary_destructive",
          description:
            "Are you sure you want to Enable Electronic Ordering for the selected vendor product?",
          submitLabel: t("modal.label.enable"),
          onSubmit: async () =>
            await patch({
              channelId: id,
              name,
              electronicOrderingActive: true,
            }),
        })
      } else {
        patch({ channelId: id, name, electronicOrderingActive: false })
      }
    },
    [t, patch],
  )

  const ps = new URLSearchParams({
    supplierId: configOwnerId,
    mediaTypeId: mediaType?.id || "",
  })

  // ** fetch media types channels (for MA Vendors)
  const { data, status } = useQuery(
    ["media-types-channels", ps.toString()],
    fetchMediaTypeChannels,
    {
      enabled: configScope === "SUPPLIER",
    },
  )

  const handleOpenChannel = (channel: Channel) => {
    setChannel(channel)
  }

  const handleCloseChannel = () => {
    setChannel(undefined)
  }

  const isLoading = status === "loading"

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <div>
          <div className={styles.button}>
            <Button variant="contained" color="primary" onClick={onOpen}>
              {t("new.product.assign")}
            </Button>
          </div>
          {!data?.length ? (
            <div className={styles.empty}>
              <span>{t("admin.mediaTypes.vendorProducts.empty")}</span>
            </div>
          ) : (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={`${styles.cell} BodyBody-sBlackBold`}>
                      {t("admin.mediaTypes.vendorProducts")}
                    </th>
                    {/* TODO: Add on the translation service, this label "Electronic Order" */}
                    {isMediaTypeTv && (
                      <th className={`${styles.cell} BodyBody-sBlackBold`}>
                        Electronic Order
                      </th>
                    )}
                    <th className={`${styles.cell} BodyBody-sBlackBold`}>
                      {t("admin.mediaTypes.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item) => {
                    return (
                      <tr key={item.id}>
                        <td className={styles.cell}>{item.name}</td>
                        {isMediaTypeTv && (
                          <td className={styles.cell}>
                            <Switch
                              checked={item.electronicOrderingActive}
                              color="primary"
                              onChange={() =>
                                onUpdateElectronicOrder(
                                  item.id,
                                  item.name,
                                  item.electronicOrderingActive,
                                )
                              }
                            />
                          </td>
                        )}
                        <td className={styles.cell}>
                          <MoreActions
                            options={[
                              {
                                label: t("vendors.options.edit"),
                                icon: <EditIcon />,
                                onClick: () => handleOpenChannel(item),
                              },
                              {
                                label: t("vendors.options.delete"),
                                icon: <DeleteIcon />,
                                onClick: () => onDelete(item.id),
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          <CreatePackageModal
            open={open}
            onClose={onClose}
            mediaTypeId={mediaType?.id || ""}
          />

          <UpdatePackageModal
            open={!!channel}
            channel={channel}
            onClose={handleCloseChannel}
            mediaTypeId={mediaType?.id || ""}
          />

          <ConfirmationModal
            onCancel={() => setModalInfo(undefined)}
            open={isModalOpen}
            {...modalInfo}
          />
        </div>
      )}
    </>
  )
}

const styles = {
  empty: css`
    display: grid;
    place-items: center;
    color: var(--dark-grey);
    height: 80px;
    font-weight: 300;
  `,
  tableContainer: css`
    margin-top: var(--spacing-xxl-2);
    min-height: 80px;
  `,
  table: css`
    width: 100%;
    border-spacing: 0;
    border-collapse: collapse;
  `,
  cell: css`
    padding: 0 2px;
    text-align: center;
    vertical-align: middle;
    font-size: 0.8125rem;
    line-height: 1.875rem;
    min-width: 90px;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    border-bottom: 1px solid var(--light-grey);

    &:first-child {
      text-align: left;
    }
  `,
  button: css`
    display: flex;
    justify-content: flex-end;
    margin: var(--spacing-xxl) 0;
  `,
}
